import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { ConfigService } from '../services/config.service';
import { ConfirmDialogueComponent } from '../components/confirm-dialogue/confirm-dialogue.component';

type ScoreColumn = {
  columnId: string;
  label: string;
  dataKey: string;
  tooltipKey: string;
};

type GenericColumn = {
  columnId: string;
  label: string;
  dataKey: string;
};

type LegendCell = {
  score: string;
  text: string;
};

type InitialAuditFilters = {
  market?: string;
  engine?: string;
  quarter?: string;
  productId?: string;
  period?: string;
};

type AuditDataLabels = {
  market: string;
  engine: string;
  quarter: string;
  product: string;
  keyCompetitor: string;
  user: string;
  notes: string;
  avgScore: string;
};

type AuditDataMapping = {
  productField: string;
  emailField: string;
  notesField: string;
  competitorSourceField: string;
  competitorPayloadField: string;
  productMetricNameField: string;
  productMetricCompetitorField: string;
};

type DropdownConfig = {
  show: boolean;
  enableSearch?: boolean;
  searchPlaceholder?: string;
  noEntriesFoundLabel?: string;
};

type EngineDropdownConfig = DropdownConfig & {
  enableSearch: boolean;
  searchPlaceholder: string;
  noEntriesFoundLabel: string;
};

type ActionButtonConfig = {
  show: boolean;
  label: string;
};

type AuditDataUiConfig = {
  marketDropdown: DropdownConfig;
  engineDropdown: EngineDropdownConfig;
  quarterDropdown: DropdownConfig;
  resetButton: ActionButtonConfig;
  saveButton: ActionButtonConfig;
  exportButton: ActionButtonConfig;
  editableScores: boolean;
};

type AuditDataConfig = {
  definitionsUrl?: string;
  labels?: Partial<AuditDataLabels>;
  displayedColumns?: string[];
  scoreColumns?: ScoreColumn[];
  genericColumns?: GenericColumn[];
  legendRows?: LegendCell[][];
  mapping?: Partial<AuditDataMapping>;
  ui?: {
    marketDropdown?: Partial<DropdownConfig>;
    engineDropdown?: Partial<EngineDropdownConfig>;
    quarterDropdown?: Partial<DropdownConfig>;
    resetButton?: Partial<ActionButtonConfig>;
    saveButton?: Partial<ActionButtonConfig>;
    exportButton?: Partial<ActionButtonConfig>;
    editableScores?: boolean;
  };
  options?: {
    market?: string[];
    engine?: string[];
    enginesByMarket?: Record<string, string[]>;
    quarter?: string[];
    useMockData?: boolean;
    marketApi?: {
      enabled?: boolean;
      responsePath?: string;
    };
    filterKeys?: {
      market?: string;
      engine?: string;
      quarter?: string;
    };
  };
  rows?: any[];
  quarterRows?: Record<string, any[]>;
  api?: {
    read?: {
      url?: string;
      responsePath?: string;
      productIdTemplate?: string;
      payload?: any;
    };
    productMetric?: {
      url?: string;
      method?: 'post' | 'get';
      payload?: any;
      responsePath?: string;
      quarterKeys?: string[];
    };
  };
};

const EMPTY_LABELS: AuditDataLabels = {
  market: '',
  engine: '',
  quarter: '',
  product: '',
  keyCompetitor: '',
  user: '',
  notes: '',
  avgScore: '',
};

const EMPTY_MAPPING: AuditDataMapping = {
  productField: '',
  emailField: '',
  notesField: '',
  competitorSourceField: '',
  competitorPayloadField: 'keycompetitor',
  productMetricNameField: '',
  productMetricCompetitorField: '',
};

const EMPTY_UI: AuditDataUiConfig = {
  marketDropdown: { show: false },
  engineDropdown: {
    show: false,
    enableSearch: false,
    searchPlaceholder: '',
    noEntriesFoundLabel: '',
  },
  quarterDropdown: { show: false },
  resetButton: { show: false, label: '' },
  saveButton: { show: false, label: '' },
  exportButton: { show: true, label: '' },
  editableScores: false,
};

@Component({
  selector: 'app-audit-data',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    MatDialogModule,
    MatSnackBarModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './audit-data.component.html',
  styleUrl: './audit-data.component.scss',
})
export class AuditDataComponent implements OnInit {
  @Input() set data(value: unknown) {
    this.widgetConfig =
      value && typeof value === 'object' ? (value as AuditDataConfig) : {};

    if (!this.initialized) {
      return;
    }

    this.applyConfig(this.widgetConfig);
  }

  @Input() set initialFilters(value: InitialAuditFilters | null | undefined) {
    this.pendingInitialFilters = value || {};

    if (this.initialized) {
      this.applyInitialSelections();
    }
  }

  private initialized = false;
  private widgetConfig: AuditDataConfig = {};
  private pendingInitialFilters: InitialAuditFilters = {};
  private sourceRows: any[] = [];
  private sourceRowsByQuarter: Record<string, any[]> = {};

  definitionsUrl = '';
  labels: AuditDataLabels = { ...EMPTY_LABELS };
  displayedColumns: string[] = [];
  scoreColumns: ScoreColumn[] = [];
  genericColumns: GenericColumn[] = [];
  legendRows: LegendCell[][] = [];
  mapping: AuditDataMapping = { ...EMPTY_MAPPING };
  uiConfig: AuditDataUiConfig = {
    marketDropdown: { ...EMPTY_UI.marketDropdown },
    engineDropdown: { ...EMPTY_UI.engineDropdown },
    quarterDropdown: { ...EMPTY_UI.quarterDropdown },
    resetButton: { ...EMPTY_UI.resetButton },
    saveButton: { ...EMPTY_UI.saveButton },
    exportButton: { ...EMPTY_UI.exportButton },
    editableScores: EMPTY_UI.editableScores,
  };

  market: string[] = [];
  filteredMarket: string[] = [];
  engine: string[] = [];
  filteredEngines: string[] = [];
  quarterOptions: string[] = [];
  filteredQuarterOptions: string[] = [];
  selectedMarket = '';
  selectedEngine = '';
  selectedQuarter = '';
  marketSearchText = '';
  quarterSearchText = '';
  enginesByMarket: Record<string, string[]> = {};
  marketApiConfig: { enabled: boolean; responsePath: string } = {
    enabled: true,
    responsePath: 'responseList.0.responseData.0.segments',
  };
  useMockData = false;
  filterKeys: { market: string; engine: string; quarter: string } = {
    market: '',
    engine: '',
    quarter: '',
  };

  dataSource: any[] = [];
  averageValues: Record<string, number> = {};
  def: Record<string, string> | null = null;
  selectedProductMetric: any = null;
  private surveyProductEngineOptions: string[] | null = null;

  constructor(
    private service: ConfigService,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.applyConfig(this.widgetConfig);
    this.initialized = true;
  }

  private applyConfig(config: AuditDataConfig): void {
    this.definitionsUrl = config.definitionsUrl ?? '';
    this.labels = {
      market: config.labels?.market ?? '',
      engine: config.labels?.engine ?? '',
      quarter: config.labels?.quarter ?? '',
      product: config.labels?.product ?? '',
      keyCompetitor: config.labels?.keyCompetitor ?? '',
      user: config.labels?.user ?? '',
      notes: config.labels?.notes ?? '',
      avgScore: config.labels?.avgScore ?? '',
    };
    this.displayedColumns = Array.isArray(config.displayedColumns)
      ? [...config.displayedColumns]
      : [];
    this.scoreColumns = Array.isArray(config.scoreColumns)
      ? [...config.scoreColumns]
      : [];
    this.genericColumns = Array.isArray(config.genericColumns)
      ? [...config.genericColumns]
      : [];
    this.legendRows = Array.isArray(config.legendRows) ? [...config.legendRows] : [];
    this.mapping = {
      productField: config.mapping?.productField ?? '',
      emailField: config.mapping?.emailField ?? '',
      notesField: config.mapping?.notesField ?? '',
      competitorSourceField: config.mapping?.competitorSourceField ?? '',
      competitorPayloadField: (config.mapping as any)?.competitorPayloadField ?? 'keycompetitor',
      productMetricNameField: config.mapping?.productMetricNameField ?? '',
      productMetricCompetitorField: config.mapping?.productMetricCompetitorField ?? '',
    };
    this.uiConfig = {
      marketDropdown: {
        show: config.ui?.marketDropdown?.show ?? false,
        enableSearch: config.ui?.marketDropdown?.enableSearch ?? false,
        searchPlaceholder: config.ui?.marketDropdown?.searchPlaceholder ?? '',
        noEntriesFoundLabel: config.ui?.marketDropdown?.noEntriesFoundLabel ?? '',
      },
      engineDropdown: {
        show: config.ui?.engineDropdown?.show ?? false,
        enableSearch: config.ui?.engineDropdown?.enableSearch ?? false,
        searchPlaceholder: config.ui?.engineDropdown?.searchPlaceholder ?? '',
        noEntriesFoundLabel: config.ui?.engineDropdown?.noEntriesFoundLabel ?? '',
      },
      quarterDropdown: {
        show: config.ui?.quarterDropdown?.show ?? false,
        enableSearch: config.ui?.quarterDropdown?.enableSearch ?? false,
        searchPlaceholder: config.ui?.quarterDropdown?.searchPlaceholder ?? '',
        noEntriesFoundLabel: config.ui?.quarterDropdown?.noEntriesFoundLabel ?? '',
      },
      resetButton: {
        show: config.ui?.resetButton?.show ?? false,
        label: config.ui?.resetButton?.label ?? '',
      },
      saveButton: {
        show: config.ui?.saveButton?.show ?? false,
        label: config.ui?.saveButton?.label ?? '',
      },
      exportButton: {
        show: config.ui?.exportButton?.show ?? true,
        label: config.ui?.exportButton?.label ?? '',
      },
      editableScores: config.ui?.editableScores ?? false,
    };

    this.market = Array.isArray(config.options?.market) ? [...config.options!.market] : [];
    this.filteredMarket = [...this.market];
    this.engine = Array.isArray(config.options?.engine) ? [...config.options!.engine] : [];
    this.enginesByMarket = config.options?.enginesByMarket ?? {};
    this.quarterOptions = Array.isArray(config.options?.quarter)
      ? [...config.options!.quarter]
      : [];
    this.filteredQuarterOptions = [...this.quarterOptions];
    this.sourceRowsByQuarter = this.normalizeQuarterRows(config.quarterRows);
    if (!this.quarterOptions.length && Object.keys(this.sourceRowsByQuarter).length) {
      this.quarterOptions = Object.keys(this.sourceRowsByQuarter);
    }
    this.marketApiConfig = {
      enabled: config.options?.marketApi?.enabled ?? true,
      responsePath:
        config.options?.marketApi?.responsePath ??
        'responseList.0.responseData.0.segments',
    };
    this.useMockData = config.options?.useMockData ?? false;
    this.filterKeys = {
      market: config.options?.filterKeys?.market ?? '',
      engine: config.options?.filterKeys?.engine ?? '',
      quarter: config.options?.filterKeys?.quarter ?? '',
    };

    this.sourceRows = Array.isArray(config.rows) ? [...config.rows] : [];
    this.selectedQuarter = this.quarterOptions[0] ?? '';
    this.loadDefinitions();
    this.loadMarketOptions();
  }

  private normalizeQuarterRows(
    quarterRows?: Record<string, any[]> | null
  ): Record<string, any[]> {
    if (!quarterRows || typeof quarterRows !== 'object' || Array.isArray(quarterRows)) {
      return {};
    }

    return Object.entries(quarterRows).reduce((acc, [quarter, rows]) => {
      acc[String(quarter)] = Array.isArray(rows) ? rows.map((row) => ({ ...row })) : [];
      return acc;
    }, {} as Record<string, any[]>);
  }

  private getActiveRows(): any[] {
    if (Object.keys(this.sourceRowsByQuarter).length) {
      const quarterKey = this.findQuarterKey(this.selectedQuarter);
      return quarterKey ? [...(this.sourceRowsByQuarter[quarterKey] ?? [])] : [];
    }

    return [...this.sourceRows];
  }

  private findQuarterKey(selectedQuarter: string): string | undefined {
    const normalized = String(selectedQuarter ?? '').trim().toLowerCase();
    if (!normalized) return undefined;

    return Object.keys(this.sourceRowsByQuarter).find(
      (quarter) => String(quarter).trim().toLowerCase() === normalized
    );
  }

  private loadDefinitions(): void {
    if (!this.definitionsUrl) {
      this.def = null;
      return;
    }
    this.service.getDef(this.definitionsUrl).subscribe({
      next: (def) => (this.def = (def as Record<string, string>) ?? null),
      error: () => (this.def = null),
    });
  }

  private updateEngineByMarket(): void {
    const scoped = this.enginesByMarket[this.selectedMarket];
    if (Array.isArray(scoped) && scoped.length) {
      this.engine = [...scoped];
    }
    this.filteredEngines = [...this.engine];
  }

  private byPath(source: any, path: string): any {
    if (!path) return source;
    return String(path)
      .split('.')
      .reduce((curr: any, key: string) => {
        if (curr === null || curr === undefined) return undefined;
        return /^\d+$/.test(key) ? curr[Number(key)] : curr[key];
      }, source);
  }

  private loadMarketOptions(): void {
    if (!this.marketApiConfig.enabled) {
      this.loadEngineOptions(this.market[0] ? [this.market[0]] : undefined);
      return;
    }

    this.service.getMarketSegments().subscribe({
      next: (res: any) => {
        const segments = this.byPath(res, this.marketApiConfig.responsePath);
        if (Array.isArray(segments) && segments.length) {
          this.market = [...segments];
        }
        this.filteredMarket = [...this.market];
        this.selectedMarket = this.resolvePreferredOption(this.market, this.pendingInitialFilters.market) || this.market[0] || '';
        this.loadEngineOptions(this.selectedMarket ? [this.selectedMarket] : undefined);
      },
      error: () => {
        this.filteredMarket = [...this.market];
        this.selectedMarket = this.resolvePreferredOption(this.market, this.pendingInitialFilters.market) || this.market[0] || '';
        this.loadEngineOptions(this.selectedMarket ? [this.selectedMarket] : undefined);
      },
    });
  }

  private loadEngineOptions(marketSeg?: string[]): void {
    this.service.getEngineModels(marketSeg).subscribe({
      next: (res: any) => {
        const engineModels = this.byPath(res, 'responseList.0.responseData.0.engineModels');
        if (Array.isArray(engineModels) && engineModels.length) {
          this.engine = [...engineModels];
          this.filteredEngines = [...this.engine];
          if (!this.selectedEngine || !this.engine.includes(this.selectedEngine)) {
            this.selectedEngine = this.resolvePreferredOption(this.engine, this.pendingInitialFilters.engine) || this.engine[0] || '';
          }
        }
        this.initializeSelections();
      },
      error: () => {
        if (!this.filteredEngines.length) {
          this.filteredEngines = [...this.engine];
        }
        this.initializeSelections();
      },
    });
  }

  private initializeSelections(): void {
    this.filteredMarket = [...this.market];
    this.filteredEngines = [...this.engine];
    this.selectedMarket = this.resolvePreferredOption(this.market, this.pendingInitialFilters.market) || this.selectedMarket || this.market[0] || '';
    this.selectedEngine = this.resolvePreferredOption(this.engine, this.pendingInitialFilters.engine) || this.selectedEngine || this.engine[0] || '';
    this.selectedQuarter = this.resolvePreferredOption(this.quarterOptions, this.pendingInitialFilters.quarter) || this.quarterOptions[0] || '';
    this.filteredQuarterOptions = [...this.quarterOptions];
    this.loadProductMetricData();
    this.loadSurveyData();
  }

  private applyInitialSelections(): void {
    const nextMarket = this.resolvePreferredOption(this.market, this.pendingInitialFilters.market);
    const nextEngine = this.resolvePreferredOption(this.engine, this.pendingInitialFilters.engine);
    const nextQuarter = this.resolvePreferredOption(this.quarterOptions, this.pendingInitialFilters.quarter);
    let changed = false;

    if (nextMarket && nextMarket !== this.selectedMarket) {
      this.selectedMarket = nextMarket;
      this.loadEngineOptions(this.selectedMarket ? [this.selectedMarket] : undefined);
      return;
    }

    if (nextEngine && nextEngine !== this.selectedEngine) {
      this.selectedEngine = nextEngine;
      changed = true;
    }

    if (nextQuarter && nextQuarter !== this.selectedQuarter) {
      this.selectedQuarter = nextQuarter;
      changed = true;
    }

    if (changed) {
      this.loadProductMetricData();
      this.loadSurveyData();
    }
  }

  private resolvePreferredOption(options: string[], preferred?: string): string {
    const value = String(preferred || '').trim();
    if (!value) return '';

    return (
      options.find((option) => String(option).trim() === value) ||
      options.find((option) => String(option).trim().toLowerCase() === value.toLowerCase()) ||
      value
    );
  }

  private loadSurveyData(): void {
    const readApiConfig = this.widgetConfig.api?.read;

    if (!this.selectedQuarter && !readApiConfig?.payload) {
      this.dataSource = this.getActiveRows();
      this.calculateAverages();
      return;
    }

    if (this.useMockData) {
      this.dataSource = this.buildMockRows(
        this.selectedMarket,
        this.selectedEngine,
        this.selectedQuarter
      );
      this.calculateAverages();
      this.cdr.detectChanges();
      return;
    }

    if (readApiConfig?.payload) {
      const payload = this.buildConfiguredPayload(readApiConfig.payload);

      const url = readApiConfig.url || '/spriced/platform';
      
      this.service.customApi(url, payload, 'post').subscribe({
        next: (res: any) => {
          console.log('loadSurveyData API success', res);
          const responsePath = readApiConfig.responsePath || 'responseList.0.survey_history.0.userResponses';
          const userResponses = this.byPath(res, responsePath) ?? this.byPath(res, 'responseList.0.responseData') ?? this.byPath(res, 'responseList.0');
          this.dataSource = Array.isArray(userResponses)
            ? userResponses.map((row: any) => this.applyProductMetricToRow({ ...row }))
            : [];
          this.calculateAverages();
          this.cdr.detectChanges();
        },
        error: () => {
          console.log('loadSurveyData API error');
          this.dataSource = this.getActiveRows();
          this.calculateAverages();
          this.cdr.detectChanges();
        }
      });
    } else {
      const selectedProductId =
        this.selectedMarket && this.selectedEngine
          ? `${this.selectedEngine} ${this.selectedMarket}`
          : '';

      this.service.readSurveyDataByQuarter(this.selectedQuarter, selectedProductId).subscribe({
        next: (res: any) => {
          console.log('loadSurveyData API success', res);
          const responseList = this.byPath(res, 'responseList.0');
          const userResponses =
            this.byPath(res, 'responseList.0.survey_history.0.userResponses') ??
            this.byPath(res, 'responseList.0.responseData') ??
            responseList;
          this.dataSource = Array.isArray(userResponses)
            ? userResponses.map((row) => ({ ...row }))
            : [];
          this.calculateAverages();
          this.cdr.detectChanges();
        },
        error: () => {
          console.log('loadSurveyData API error');
          this.dataSource = this.getActiveRows();
          this.calculateAverages();
          this.cdr.detectChanges();
        },
      });
    }
  }

  getEngine(): void {
    this.loadEngineOptions(this.selectedMarket ? [this.selectedMarket] : undefined);
  }

  filterMarketSelection(text: string): void {
    const query = String(text ?? '').trim().toLowerCase();
    this.filteredMarket = this.market.filter((item) =>
      String(item).toLowerCase().includes(query)
    );
  }

  onEngineChange(): void {
    this.loadProductMetricData();
    this.loadSurveyData();
  }

  onQuarterChange(): void {
    console.log('Quarter changed to:', this.selectedQuarter);
    this.loadSurveyData();
    this.calculateAverages();
  }

  filterQuarterSelection(text: string): void {
    const query = String(text ?? '').trim().toLowerCase();
    this.filteredQuarterOptions = this.quarterOptions.filter((item) =>
      String(item).toLowerCase().includes(query)
    );
  }

  filterEngineSelection(text: string): void {
    const query = String(text ?? '').trim().toLowerCase();
    this.filteredEngines = this.engine.filter((item) =>
      String(item).toLowerCase().includes(query)
    );
  }

  private loadProductMetricData(): void {
    const productMetricConfig = this.widgetConfig.api?.productMetric;
    if (!productMetricConfig?.payload) {
      this.selectedProductMetric = null;
      return;
    }

    const isSurveyProducts = productMetricConfig.payload?.endpoints?.some(
      (endpoint: any) => endpoint?.functionName === 'SurveyProducts'
    );
    if (isSurveyProducts && !this.surveyProductEngineOptions) {
      this.service.getEngineModels(this.market).subscribe({
        next: (response: any) => {
          const options = this.byPath(response, 'responseList.0.responseData.0.engineModels');
          this.surveyProductEngineOptions = Array.isArray(options)
            ? Array.from(new Set(options.map((value: any) => String(value).trim()).filter(Boolean)))
            : [];
          this.requestProductMetricData(productMetricConfig, this.surveyProductEngineOptions);
        },
        error: () => {
          this.selectedProductMetric = null;
        },
      });
      return;
    }

    this.requestProductMetricData(productMetricConfig, this.surveyProductEngineOptions || undefined);
  }

  private requestProductMetricData(
    productMetricConfig: NonNullable<NonNullable<AuditDataConfig['api']>['productMetric']>,
    engineOptions?: string[]
  ): void {
    const marketOptions = this.market.map((value) => {
      const normalized = String(value).trim().toUpperCase();
      return normalized === 'O&G' ? 'OIL & GAS' : normalized;
    });
    const payload = this.buildConfiguredPayload(productMetricConfig!.payload, {
      marketOptions,
      engineOptions: engineOptions || [],
    });
    const url = productMetricConfig.url || '/spriced/platform';
    const method = productMetricConfig.method || 'post';

    this.service.customApi(url, payload, method).subscribe({
      next: (res: any) => {
        const responsePath = productMetricConfig.responsePath || 'responseList.0.responseData.0';
        const metricRoot = this.byPath(res, responsePath) ?? null;
        this.selectedProductMetric = this.resolveProductMetric(metricRoot, productMetricConfig);
        this.dataSource = this.dataSource.map((row) => this.applyProductMetricToRow({ ...row }));
        this.cdr.detectChanges();
      },
      error: () => {
        this.selectedProductMetric = null;
      },
    });
  }

  private buildConfiguredPayload(payloadTemplate: any, extraContext: Record<string, any> = {}): any {
    const context: Record<string, any> = {
      ...extraContext,
      market: this.selectedMarket,
      engine: this.selectedEngine,
      quarter: this.selectedQuarter,
      period: this.pendingInitialFilters.period || '',
      productId: this.pendingInitialFilters.productId &&
        this.selectedMarket === this.pendingInitialFilters.market &&
        this.selectedEngine === this.pendingInitialFilters.engine
          ? this.pendingInitialFilters.productId
          : `${this.selectedEngine} ${this.selectedMarket}`.trim(),
    };
    const resolve = (value: any): any => {
      if (Array.isArray(value)) return value.map(resolve);
      if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([key, nested]) => [key, resolve(nested)]));
      }
      if (typeof value !== 'string') return value;

      const exactToken = value.match(/^\{\{\s*([^}]+?)\s*\}\}$/);
      if (exactToken && Object.prototype.hasOwnProperty.call(context, exactToken[1])) {
        return context[exactToken[1]];
      }

      return value.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_match, token) =>
        String(context[token] ?? '')
      );
    };

    return resolve(payloadTemplate ?? {});
  }

  private resolveProductMetric(metricRoot: any, productMetricConfig: NonNullable<AuditDataConfig['api']>['productMetric']): any {
    if (!metricRoot || typeof metricRoot !== 'object') return null;

    const quarterKeys = Array.isArray(productMetricConfig?.quarterKeys)
      ? productMetricConfig.quarterKeys
      : [];

    for (const key of quarterKeys) {
      const metricRows = metricRoot?.[key];
      if (Array.isArray(metricRows) && metricRows.length) {
        return metricRows[0];
      }
    }

    return metricRoot;
  }

  private applyProductMetricToRow(row: any): any {
    if (!this.selectedProductMetric || !row || typeof row !== 'object') return row;

    const productField = this.mapping.productField;
    const competitorField = this.mapping.competitorSourceField;
    const productMetricNameField = this.mapping.productMetricNameField;
    const productMetricCompetitorField = this.mapping.productMetricCompetitorField;

    if (productField && productMetricNameField && !String(row[productField] ?? '').trim()) {
      row[productField] = this.selectedProductMetric?.[productMetricNameField] ?? row[productField];
    }

    if (competitorField && productMetricCompetitorField && !String(row[competitorField] ?? '').trim()) {
      row[competitorField] =
        this.selectedProductMetric?.[productMetricCompetitorField] ?? row[competitorField];
    }

    return row;
  }

  calculateAverages(): void {
    this.averageValues = {};
    this.scoreColumns.forEach((column) => {
      const key = column.dataKey;
      const nums = this.dataSource
        .map((row) => Number(row?.[key]))
        .filter((v) => Number.isFinite(v) && v > 0);
      const total = nums.reduce((sum, val) => sum + val, 0);
      this.averageValues[key] = nums.length ? total / nums.length : 0;
    });
  }

  getToolTip(name: string): string {
    if (!name || !this.def) return '';
    return this.def[name] || '';
  }

  exportAvgScores(): void {
    const quarter = this.selectedQuarter || 'Q22025';
    this.service.exportAvgSurveyScores(quarter).subscribe({
      next: (response: any) => {
        const blob: Blob | undefined = response?.body;
        if (!blob) return;

        const fallbackName = 'avg-scores-export.xlsx';
        const contentDisposition = response?.headers?.get?.('Content-Disposition');
        const responseFileName = this.resolveFilename(contentDisposition, fallbackName);
        const fileName = this.ensureXlsxFileName(responseFileName);
        const excelBlob = new Blob([blob], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        this.downloadBlob(
          excelBlob,
          fileName
        );
      },
      error: () => {
        // no-op
      },
    });
  }

  exportQuarterData(): void {
    const quarter = this.selectedQuarter || 'Q22025';
    this.service.exportQuarterData(quarter).subscribe({
      next: (response: any) => {
        const blob: Blob | undefined = response?.body;
        if (!blob) return;

        const fallbackName = `quarter-data-${quarter}.xlsx`;
        const contentDisposition = response?.headers?.get?.('Content-Disposition');
        const responseFileName = this.resolveFilename(contentDisposition, fallbackName);
        const fileName = this.ensureXlsxFileName(responseFileName);
        const excelBlob = new Blob([blob], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        this.downloadBlob(excelBlob, fileName);
      },
      error: () => {
        // no-op: quarter export is backend-only
      },
    });
  }

  private buildQuarterBucketRows(): string[][] {
    const rows: string[][] = [];
    Object.entries(this.sourceRowsByQuarter).forEach(([quarter, quarterRows]) => {
      (quarterRows ?? []).forEach((row) => {
        rows.push(this.mapDataRowToCsvRow(row, quarter));
      });
    });
    return rows;
  }

  private buildCurrentDataRows(): string[][] {
    return this.dataSource.map((row) =>
      this.mapDataRowToCsvRow(row, this.selectedQuarter || 'N/A')
    );
  }

  private mapDataRowToCsvRow(row: any, quarter: string): string[] {
    return [
      String(quarter),
      String(row?.[this.mapping.productField] ?? ''),
      String(row?.[this.mapping.competitorSourceField] ?? ''),
      String(row?.[this.mapping.emailField] ?? ''),
      ...this.scoreColumns.map((column) => String(row?.[column.dataKey] ?? '')),
      ...this.genericColumns.map((column) => String(row?.[column.dataKey] ?? '')),
      String(row?.[this.mapping.notesField] ?? ''),
    ];
  }

  saveData(): void {
    const payload = this.dataSource.map((row: any) => ({
      product_id: row?.[this.mapping.productField],
      email: row?.[this.mapping.emailField],
      TCO: row?.TCO,
      affordability: row?.affordability,
      availability: row?.availability,
      productivity: row?.productivity,
      sustainability: row?.sustainability,
      [this.mapping.competitorPayloadField || 'keycompetitor']:
        row?.[this.mapping.competitorSourceField],
      notes: row?.[this.mapping.notesField],
    }));

    this.service.saveApi(payload).subscribe({
      next: () => {
        this.sourceRows = this.dataSource.map((row) => ({ ...row }));
        this.snackBar.open('Survey saved successfully!', 'Close', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['snackbar-success'],
        });
      },
      error: () => {
        this.snackBar.open('Failed to save survey.', 'Close', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['snackbar-error'],
        });
      },
    });
  }

  deleteRow(index: number): void {
    const dialogRef = this.dialog.open(ConfirmDialogueComponent, {
      width: '400px',
      data: { title: 'Delete', desc: 'The row will be deleted' },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.dataSource.splice(index, 1);
      this.dataSource = [...this.dataSource];
      this.sourceRows = this.dataSource.map((row) => ({ ...row }));
      this.calculateAverages();
    });
  }

  private downloadCsv(headers: string[], rows: string[][], fileName: string): void {
    const csvLines = [
      headers.map((header) => this.escapeCsv(header)).join(','),
      ...rows.map((row) => row.map((cell) => this.escapeCsv(cell)).join(',')),
    ];
    const csvContent = '\ufeff' + csvLines.join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private downloadTextAsCsv(content: string, fileName: string): void {
    const normalized = this.normalizeCsvText(content);
    const blob = new Blob(['\ufeff' + normalized], { type: 'text/csv;charset=utf-8;' });
    this.downloadBlob(blob, this.ensureCsvFileName(fileName), 'text/csv;charset=utf-8;');
  }

  private downloadBlob(blob: Blob, fileName: string, type?: string): void {
    const downloadBlob = type ? new Blob([blob], { type }) : blob;
    const url = window.URL.createObjectURL(downloadBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private decodeBase64ToText(base64: string): string {
    const normalizedBase64 = String(base64 ?? '').replace(/\s/g, '');
    const padding = normalizedBase64.length % 4;
    const paddedBase64 =
      padding === 0 ? normalizedBase64 : normalizedBase64 + '='.repeat(4 - padding);
    try {
      return decodeURIComponent(
        Array.prototype.map
          .call(atob(paddedBase64), (char: string) =>
            `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`
          )
          .join('')
      );
    } catch {
      return atob(paddedBase64);
    }
  }

  private safeParseJson(raw: string): any {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  private resolveFilename(contentDisposition: string | null, fallbackName: string): string {
    if (!contentDisposition) return fallbackName;
    const match = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^\";]+)/i);
    if (!match?.[1]) return fallbackName;
    return decodeURIComponent(match[1].replace(/"/g, '').trim());
  }

  private ensureCsvFileName(fileName: string): string {
    const name = String(fileName || '').trim() || 'avg-scores-export.csv';
    return name.toLowerCase().endsWith('.csv') ? name : `${name}.csv`;
  }

  private ensureXlsxFileName(fileName: string): string {
    const name = String(fileName || '').trim() || 'avg-scores-export.xlsx';
    return name.toLowerCase().endsWith('.xlsx') ? name : `${name}.xlsx`;
  }

  private buildCsvFromApiPayload(payload: any): string | null {
    if (!payload || typeof payload !== 'object') return null;

    const candidateRows =
      this.extractObjectArray(payload?.responseList?.[0]?.responseData) ??
      this.extractObjectArray(payload?.responseData) ??
      this.extractObjectArray(payload?.data?.responseData) ??
      this.extractObjectArray(payload?.data) ??
      this.extractObjectArray(payload);

    if (!candidateRows?.length) return null;

    const headerSet = new Set<string>();
    candidateRows.forEach((row) => Object.keys(row || {}).forEach((k) => headerSet.add(k)));
    const headers = Array.from(headerSet);
    if (!headers.length) return null;

    const lines = [
      headers.map((h) => this.escapeCsv(h)).join(','),
      ...candidateRows.map((row) =>
        headers.map((h) => this.escapeCsv(String(row?.[h] ?? ''))).join(',')
      ),
    ];
    return lines.join('\r\n');
  }

  private extractObjectArray(value: any): Array<Record<string, unknown>> | null {
    if (Array.isArray(value)) {
      if (value.length && value.every((item) => item && typeof item === 'object' && !Array.isArray(item))) {
        return value as Array<Record<string, unknown>>;
      }

      for (const item of value) {
        const nested = this.extractObjectArray(item);
        if (nested?.length) return nested;
      }
      return null;
    }

    if (value && typeof value === 'object') {
      for (const nestedValue of Object.values(value)) {
        const nested = this.extractObjectArray(nestedValue);
        if (nested?.length) return nested;
      }
    }

    return null;
  }

  private looksLikeCsvText(value: string): boolean {
    const text = String(value ?? '').trim();
    if (!text) return false;
    if (text.includes('\u0000')) return false;
    const firstLine = text.split(/\r?\n/, 1)[0] ?? '';
    return firstLine.includes(',') || firstLine.includes(';') || firstLine.includes('\t');
  }

  private normalizeCsvText(content: string): string {
    let text = String(content ?? '');
    // Some APIs return CSV as escaped JSON-like text: "a,b\\nc,d"
    if (!text.includes('\n') && text.includes('\\n')) {
      text = text.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n');
    }
    // Normalize line endings for spreadsheet compatibility.
    return text.replace(/\r?\n/g, '\r\n');
  }

  private downloadAvgScoresFromCurrentView(fileName: string): void {
    const headers = ['Scope', ...this.scoreColumns.map((column) => column.label)];
    const averages = this.scoreColumns.map((column) => {
      const value = Number(this.averageValues?.[column.dataKey] ?? 0);
      return Number.isFinite(value) ? value.toFixed(1) : '0.0';
    });
    const scopeLabel =
      [this.selectedMarket, this.selectedEngine, this.selectedQuarter]
        .filter((part) => String(part ?? '').trim().length > 0)
        .join(' | ') || 'Current View';

    this.downloadCsv(headers, [[scopeLabel, ...averages]], fileName.replace(/\.csv$/i, ''));
  }

  private escapeCsv(value: string): string {
    const text = String(value ?? '');
    if (/[",\n]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  }

  private buildMockRows(market: string, engine: string, quarter: string): any[] {
    const quarterFactor = this.getQuarterFactor(quarter);
    const productTags = ['A', 'B', 'C'];

    return productTags.map((tag, index) => {
      const base = 5 + ((quarterFactor + index) % 3);

      return {
        [this.mapping.productField]: `${engine} ${market} ${tag}`,
        [this.mapping.competitorSourceField]: `Competitor ${index + 1}`,
        [this.mapping.emailField]: `user${index + 1}@example.com`,
        TCO: this.boundScore(base + 1),
        affordability: this.boundScore(base),
        availability: this.boundScore(base + 2),
        sustainability: this.boundScore(base - 1),
        productivity: this.boundScore(base + 1),
        [this.mapping.notesField]: `Mock data for ${quarter} (${tag})`,
      };
    });
  }

  private getQuarterFactor(quarter: string): number {
    const value = String(quarter ?? '').toUpperCase();
    if (value.startsWith('Q1')) return 1;
    if (value.startsWith('Q2')) return 2;
    if (value.startsWith('Q3')) return 3;
    if (value.startsWith('Q4')) return 4;
    return 0;
  }

  private boundScore(value: number): number {
    return Math.max(1, Math.min(10, value));
  }

  private getRowsForSegment$(segment: string): Observable<any[]> {
    if (this.useMockData) {
      const engines = this.resolveMockEnginesForSegment(segment);
      const rows = engines.reduce(
        (acc: any[], engine: string) => [
          ...acc,
          ...this.buildMockRows(segment, engine, this.selectedQuarter),
        ],
        []
      );
      return of(rows);
    }

    return this.service.getEngineModels([segment]).pipe(
      map((res: any) => {
        const engineModels = this.byPath(res, 'responseList.0.responseData.0.engineModels');
        return Array.isArray(engineModels) ? engineModels : [];
      }),
      switchMap((engines: string[]) => {
        if (!engines.length) {
          return of([]);
        }

        const readRequests = engines.map((engine) =>
          this.service.readApi(segment, engine, this.selectedQuarter).pipe(
            map((res: any) => {
              const userResponses = this.byPath(
                res,
                'responseList.0.survey_history.0.userResponses'
              );
              return Array.isArray(userResponses) ? userResponses : [];
            }),
            catchError(() => of([]))
          )
        );

        return forkJoin(readRequests).pipe(
          map((responseSets) =>
            responseSets.reduce((acc: any[], rows: any[]) => [...acc, ...rows], [])
          )
        );
      }),
      catchError(() => of([]))
    );
  }

  private resolveMockEnginesForSegment(segment: string): string[] {
    const scoped = this.enginesByMarket?.[segment];
    if (Array.isArray(scoped) && scoped.length) {
      return scoped;
    }

    if (this.selectedEngine) {
      return [this.selectedEngine];
    }

    return this.engine.length ? [...this.engine] : ['Engine'];
  }
}
